import { ErrorType } from "@/hooks/useValidation"
import ErrorHandlerService from "@/services/ErrorHandler"

const SERVICE_WORKER_FILE_PATH = `${process.env.PUBLIC_URL}/service-worker.js`
const SERVICE_WORKER_SCRIPT_CONTENT_TYPE = "javascript"
const SERVICE_WORKER_NOT_FOUND_STATUS = 404

class ServiceWorkerRegistrator {
	private readonly serviceWorkerUrl = SERVICE_WORKER_FILE_PATH

	private get isLocalhost (): boolean {
		const isLocalhostIPv4 = /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(
			window.location.hostname
		)

		return Boolean(
			window.location.hostname === "localhost" ||
			window.location.hostname === "[::1]" ||
			isLocalhostIPv4
		)
	}

	private get isSameOrigin (): boolean {
		const publicUrl = new URL(process.env.PUBLIC_URL || "", window.location.href)
		return publicUrl.origin === window.location.origin
	}

	private get isSupportedBrowser (): boolean {
		return "serviceWorker" in navigator
	}

	private isInvalidServiceWorkerResponse (response: Response): boolean {
		const contentType = response.headers.get("content-type")
		const isNotFound = response.status === SERVICE_WORKER_NOT_FOUND_STATUS
		const isNotScript = contentType != null && !contentType.includes(SERVICE_WORKER_SCRIPT_CONTENT_TYPE)
		return isNotFound || isNotScript
	}

	private registerValidServiceWorker (): void {
		navigator.serviceWorker
			.register(this.serviceWorkerUrl)
			.catch(error => {
				ErrorHandlerService.handle(error as ErrorType)
			})
	}

	private unregisterStaleAndReload (registration: ServiceWorkerRegistration): void {
		registration.unregister().then(() => window.location.reload())
	}

	private checkValidServiceWorker (): void {
		fetch(this.serviceWorkerUrl, { headers: { "Service-Worker": "script" } })
			.then((response) => {
				if (this.isInvalidServiceWorkerResponse(response)) {
					navigator.serviceWorker.ready.then(registration => this.unregisterStaleAndReload(registration))
					return
				}

				this.registerValidServiceWorker()
			})
			.catch(error => {
				ErrorHandlerService.handle(error as ErrorType)
			})
	}

	register (): void {
		if (!this.isSupportedBrowser || !this.isSameOrigin) return

		window.addEventListener("load", () => {
			if (this.isLocalhost) {
				this.checkValidServiceWorker()
			} else {
				this.registerValidServiceWorker()
			}
		})
	}

	unregister (): void {
		if (!this.isSupportedBrowser) return

		navigator.serviceWorker.ready
			.then((registration) => registration.unregister())
			.catch(error => ErrorHandlerService.handle(error as ErrorType))
	}
}

export default new ServiceWorkerRegistrator()
